import { HttpErrorResponse } from "@angular/common/http";
import { ErrorHandler, Injectable, Injector, inject } from "@angular/core";
import { createErrorHandler } from "@sentry/angular";
import { TranslateService } from "@ngx-translate/core";

import { environment } from "src/environments/environment";
import { showConnectionBanner } from "./shared/utils/helpers";
import { LoggerService } from "./shared/services/logger.service";
import { StatusCodes } from "http-status-codes";
import packageInfo from "../../package.json";
import {
  BadRequestError,
  ForbiddenError,
  NotFoundError,
  InternalServerError,
  GenericHttpError,
} from "./shared/models/error.model";

const STALE_BUNDLE_ERROR_PATTERNS = [
  /Loading chunk [^\s]+ failed[\s\S]*$/i,
  /Failed to fetch dynamically imported module/i,
  /(?:Failed to load module script:\s*)?Expected a JavaScript(?:-or-Wasm)? module script but the server responded with a MIME type/i,
  /Importing a module script failed/i,
];
const STALE_BUNDLE_RECOVERY_STORAGE_KEY = "finn:stale-bundle-recovery";

/** Detects browser-specific failures that the existing cache recovery can fix. */
function isStaleBundleError(message: unknown): boolean {
  return (
    typeof message === "string" &&
    STALE_BUNDLE_ERROR_PATTERNS.some((pattern) => pattern.test(message))
  );
}

@Injectable()
export class CompositeErrorHandler implements ErrorHandler {
  private readonly injector: Injector;

  private errorHandler: ErrorHandler;
  private readonly loggerService: LoggerService;

  constructor(...args: unknown[]);
  /* istanbul ignore next -- Angular DI migration wiring */
  constructor() {
    const [injector = inject(Injector)] = arguments as unknown as [
      Injector | undefined,
    ];

    this.injector = injector;

    if (environment.production) {
      this.errorHandler = createErrorHandler();
    } else {
      this.errorHandler = new ErrorHandler();
    }
    this.loggerService = this.injector.get(LoggerService);
  }

  private windowReload(): void {
    window.location.reload();
  }

  /** Claims one cache recovery attempt for the current app release. */
  private claimStaleBundleRecoveryAttempt(): boolean {
    try {
      if (
        window.sessionStorage.getItem(STALE_BUNDLE_RECOVERY_STORAGE_KEY) ===
        packageInfo.version
      ) {
        return false;
      }

      window.sessionStorage.setItem(
        STALE_BUNDLE_RECOVERY_STORAGE_KEY,
        packageInfo.version
      );
      return true;
    } catch {
      return false;
    }
  }

  private async handleChunkError(): Promise<void> {
    try {
      if ("caches" in window && caches) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
    } catch (cacheError) {
      console.error("Error while clearing caches:", cacheError);
    } finally {
      this.windowReload();
    }
  }

  handleError(error: any): void {
    // Handle HttpErrorResponse (Unknown Errors)
    if (error instanceof HttpErrorResponse && error.status === 0) {
      console.error(error);
      // return to escape sentry noise
      return;
    }

    // Handle Firebase Config Errors
    if (
      error.message?.includes("Remote Config: Fetch client failed to connect")
    ) {
      const translateService = this.injector.get(TranslateService);
      const connectivityErrorMessage =
        translateService.instant("CONNECTION_ISSUE");

      showConnectionBanner(connectivityErrorMessage);
      // return to escape sentry noise
      return;
    }

    // Handle Chunk Load Errors
    const errorMessage = error?.message || error?.toString();

    if (
      isStaleBundleError(errorMessage) &&
      this.claimStaleBundleRecoveryAttempt()
    ) {
      this.handleChunkError();
      // return to escape sentry noise
      return;
    }

    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);
      // return to avoid duplicate error logging to Sentry
      return;
    }

    this.errorHandler.handleError(error);
  }

  private handleHttpError(error: HttpErrorResponse): void {
    let customError: Error;
    const timestamp = new Date().toISOString();

    switch (error.status) {
      case StatusCodes.BAD_REQUEST:
        customError = new BadRequestError(error.url, error.error, timestamp);
        break;

      case StatusCodes.UNAUTHORIZED:
      case StatusCodes.TOO_MANY_REQUESTS:
        return; // return to avoid noise. This status are not important to log.

      case StatusCodes.FORBIDDEN:
        customError = new ForbiddenError(error.url, timestamp);
        break;

      case StatusCodes.NOT_FOUND:
        customError = new NotFoundError(error.url, timestamp);
        break;

      case StatusCodes.INTERNAL_SERVER_ERROR:
        customError = new InternalServerError(
          error.url,
          error.error,
          timestamp
        );
        break;

      default:
        customError = new GenericHttpError(
          error.status,
          error.url,
          error.error,
          timestamp
        );
    }

    // Send custom error to Sentry
    this.loggerService.logError(customError, {
      data: {
        errorType: "http_error",
        statusCode: error.status.toString(),
        endpoint: this.extractEndpoint(error.url),
      },
    });
  }

  private extractEndpoint(url: string): string {
    if (!url) return "unknown";
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      return url;
    }
  }
}
